import {
  getHubspotAttribution,
  syncHubspotContact,
} from '@/api/portalFramework/hubspot';
import { getRuntimeInfo } from '@/hooks/useRuntimeInfo';
import {
  getUser,
  posthog,
  trackingEvent,
  useUser,
} from '@zenlayer/portal-utils';
import { useDebounceEffect } from 'ahooks';
import { useLocation } from 'react-router-dom';

/**
 * HubSpot 的追踪队列是 `_hsq`（由 js-na2.hs-analytics.net 注入），不是 `_hsp`
 * ——`_hsp` 是 hs-banner 的 cookie consent 队列，收到 identify 会被静默丢弃，
 * 所以此前的身份关联从未生效，联系人和广告点击/落地页的行为链一直是断的。
 *
 * 另外 identify 只把身份写进 cookie，必须紧跟一次 trackPageView 才会真正提交。
 * 队列自己兜底成数组：loader 是异步注入的，脚本到达前 push 的指令由它加载后补消费。
 */
const getHsq = () => {
  if (getRuntimeInfo().online !== true) return undefined;
  const g = globalThis as typeof globalThis & { _hsq?: unknown[] };
  g._hsq = g._hsq || [];
  return g._hsq;
};

/**
 * 追踪脚本是否真的接管了队列。`_hsq` 存在与否不是判据（上面自己兜底成了数组），
 * 判据是 push 已不再是原生方法。广告/隐私拦截器拦掉 hs-scripts 时，push 永远是
 * Array.prototype.push，identify 只会堆在数组里永远发不出去。
 */
export const isHubspotTrackerActive = () => {
  const hsq = getHsq();
  return !!hsq && !String(hsq.push).includes('[native code]');
};

/**
 * 「脚本被拦截」这个结论不能在页面刚打开时下：loader 是三跳异步加载
 * （hs-scripts → na2 → hs-analytics），首屏的 getUser 往往比它先回来，那一刻 push
 * 还是原生的。误判成被拦截会立刻 upsert，正好重演要修的那场建档竞争；误判成没被
 * 拦截只是多等几秒。所以要求 load 事件已过、且页面已经存在了 TRACKER_GRACE_MS。
 */
const TRACKER_GRACE_MS = 10_000;
const trackerVerdictReady = () =>
  document.readyState === 'complete' && performance.now() > TRACKER_GRACE_MS;
/** 结论还下不了时，隔多久再看一眼 */
const RECHECK_MS = 5_000;

let lastPushed = '';

const pushIdentity = (uid?: string, email?: string, path?: string) => {
  // 官方明确要求「只在已知 email 时才 identify」：只带 id 的 identify 产生不了
  // 联系人，白发一次请求。
  if (!email) return;
  const hsq = getHsq();
  if (!hsq) return;

  /**
   * SPA 必须先 setPath 再 trackPageView：trackPageView 不读当前地址栏，只用
   * tracker 内部记着的 path，不更新的话每次路由切换都会记成脚本初始化时那个 URL
   * （污染 hs_analytics_last_url 和页内轨迹）。
   * path 不传时取当前地址——effect 在路由变更之后才跑，此时已是新地址。
   */
  const { pathname, search } = globalThis.location;
  const nextPath = path ?? `${pathname}${search}`;

  /**
   * 登录完成的瞬间，useAutoLogin 的 initHubspotTracker 与本文件的 pageview hook
   * 会先后落到同一个 (email, path) 上；去重避免同一次访问被记成两次 page view。
   */
  const key = `${email}|${nextPath}`;
  if (key === lastPushed) return;
  lastPushed = key;

  /**
   * 注册瞬间还没有 uid。不能传 `id: undefined`——若被序列化成字面量 "undefined"，
   * 所有新注册用户会共用同一个访客 id，HubSpot 侧会跨用户串档。只带 email 是
   * 官方支持的用法。
   */
  hsq.push(['identify', uid ? { id: uid, email } : { email }]);
  hsq.push(['setPath', nextPath]);
  hsq.push(['trackPageView']);
};

export function initHubspotTracker(uid?: string, email?: string) {
  pushIdentity(uid, email);
}

/**
 * 把当前登录用户的姓名补进 HubSpot。email 和姓名都由 portal-framework 从登录态取
 * （batch/upsert 的取舍与 207 部分失败的判断也在那边），所以这里不传参数，
 * 也只能写自己这一条联系人。
 */
export async function sendHubspotRegistrations() {
  let status: number | undefined;
  let code: string | undefined;

  try {
    const res = await syncHubspotContact();
    status = res.status;
    code = res.data?.code;
    // 网关拦下（401/403）时没有响应体；服务端失败是 HTTP 200 + success:false
    if (!res.data?.success) {
      throw new Error(
        `[hubspot] contact sync failed: HTTP ${status} ${code ?? ''}`
      );
    }
    return true;
  } catch (e) {
    /**
     * 上报失败不该打断注册流程，但必须留下痕迹 —— 原实现连响应码都不看。
     * 走 posthog 而不是 Sentry：注册漏斗的其它节点（signup_newRegistedUser_email
     * / _oauth）都在 posthog，落在同一处才能直接算出「注册成功但 HubSpot 没收到」
     * 的比例。properties 里不放 email——identify 已绑定用户身份，不重复带 PII。
     */
    trackingEvent({
      eventId: 'signup_hubspot_UpsertFailed',
      trackingType: 'error',
      customData: JSON.stringify({
        status,
        code,
        message: e instanceof Error ? e.message : String(e),
      }),
    });
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* 注册建档：identify 先、upsert 后                                      */
/* ------------------------------------------------------------------ */

/**
 * HubSpot 的原始来源（hs_analytics_source）在联系人建档那一刻就定了，之后只增不改。
 * 谁先建档决定了有没有归因：
 * - 追踪脚本 identify 建的（ANALYTICS）带着访客 cookie 的完整历史，OFFLINE 为 0；
 * - 注册接口 upsert 建的（INTEGRATION）是空档，若 HubSpot 那一刻还没处理完这个
 *   访客的会话，原始来源就永久停在 OFFLINE——2026-09 实测占注册上报的三成。
 * 所以注册完成时只 identify + trackPageView，让脚本先建档；补姓名的 upsert 延后。
 *
 * 延迟多久没有官方数字。实测 API 建档后归因约 6 秒就跟上，说明脚本侧处理是秒级，
 * 2 分钟是留足余量。验证方式：插件 HubSpot 面板里「注册上报写入」占比应降到
 * 追踪脚本被拦截的那部分（约四分之一），明显更高说明这里的顺序失效了。
 */
const UPSERT_DELAY_MS = 2 * 60_000;
/**
 * 待补记录的寿命。upsert 永久失败（如 HubSpot 判 email 非法返回 400）时，
 * 没有它就会在每次页面加载都重试并上报一次错误事件，无限期。
 */
const PENDING_TTL_MS = 7 * 86_400_000;

/** 只记「谁、何时注册」：姓名由服务端从 usercenter 取，不在浏览器里留存 */
type PendingSignup = {
  email: string;
  signupAt: number;
};

const pendingKey = (email: string) => `zen_hs_signup:${email.toLowerCase()}`;

const readPending = (email: string): PendingSignup | undefined => {
  try {
    const raw = localStorage.getItem(pendingKey(email));
    return raw ? (JSON.parse(raw) as PendingSignup) : undefined;
  } catch {
    return undefined;
  }
};

const writePending = (p: PendingSignup) => {
  try {
    localStorage.setItem(pendingKey(p.email), JSON.stringify(p));
  } catch {
    // Safari 隐私模式 / 企业策略下 localStorage 不可写。注册当场的那条链路靠
    // 内存里的 fallback 记录继续走，丢的只是「用户提前离开、下次登录再补」这一步。
  }
};

const clearPending = (email: string) => {
  try {
    localStorage.removeItem(pendingKey(email));
  } catch {
    /* noop */
  }
};

/** 每个 email 最多挂一个待补定时器；重复调度先清旧的，避免同一条记录被多次 upsert。 */
const flushTimers = new Map<string, ReturnType<typeof setTimeout>>();

const armFlush = (email: string, delay: number, fallback?: PendingSignup) => {
  const key = email.toLowerCase();
  const prev = flushTimers.get(key);
  if (prev !== undefined) clearTimeout(prev);
  flushTimers.set(
    key,
    globalThis.setTimeout(() => {
      flushTimers.delete(key);
      void flushPendingSignup(email, fallback);
    }, delay)
  );
};

/**
 * 把待补的姓名 upsert 到 HubSpot。满足其一才真的发：
 * - 距注册已过 UPSERT_DELAY_MS——脚本建档的窗口已经让出去了；
 * - 追踪脚本确认被拦截——identify 永远发不出去，API 是唯一能建档的路。
 * 其余情况挂定时器稍后再看。定时器只活在当前页面，所以每次页面加载
 * （useAutoLogin）都会重新走一遍这里，把定时器挂回去。
 * 成功后清掉记录；失败保留，下次再来；超过 TTL 放弃。
 *
 * @param fallback 注册当场的记录。localStorage 写不进去时靠它，否则脚本被拦截的用户
 *   会既没 identify 成功也没 upsert——比改动前还差。
 */
export async function flushPendingSignup(
  email: string,
  fallback?: PendingSignup
) {
  const pending = readPending(email) ?? fallback;
  if (!pending) return;
  const age = Date.now() - pending.signupAt;
  // 记录损坏（signupAt 不是数字）或过期：放弃，别让一条坏记录永远重试
  if (!Number.isFinite(age) || age > PENDING_TTL_MS) {
    clearPending(email);
    return;
  }

  if (age < UPSERT_DELAY_MS) {
    if (isHubspotTrackerActive()) {
      armFlush(email, UPSERT_DELAY_MS - age, fallback);
      return;
    }
    if (!trackerVerdictReady()) {
      armFlush(email, RECHECK_MS, fallback);
      return;
    }
    // 页面早已 load 完、脚本仍没接管队列：被拦截了，等也等不来，立刻走 API
  }

  /**
   * 服务端写的是「当前登录的人」，没登录态就等。注册当场登录请求还没返回、页面加载时
   * useAutoLogin 还没 setUser，这时发出去会被网关 401，而 zenFetch 遇到 401 会取消所有
   * 在飞的请求——注册当场取消的就是那次登录，用户会卡在验证码页。
   * 同一浏览器里已换成别的账号时不发，记录留给本人下次登录。
   */
  const current = getUser()?.username;
  if (!current) {
    armFlush(email, RECHECK_MS, fallback);
    return;
  }
  if (current.toLowerCase() !== pending.email.toLowerCase()) return;

  const ok = await sendHubspotRegistrations();
  if (ok) clearPending(email);
}

/**
 * 注册完成时调用（邮箱验证码通过 / OAuth 注册成功），两条注册路径都要接。
 * 先 identify 让追踪脚本建档，姓名的 upsert 交给 flushPendingSignup 按规则延后。
 */
export function onHubspotSignup(email: string) {
  if (getRuntimeInfo().online !== true) return;
  const record: PendingSignup = { email, signupAt: Date.now() };
  writePending(record);
  pushIdentity(undefined, email);
  void flushPendingSignup(email, record);
}

/* ------------------------------------------------------------------ */
/* 归因同步到 PostHog                                                    */
/* ------------------------------------------------------------------ */

/**
 * 这几个渠道的 drill-down 1 才是广告系列名（付费搜索是 Google Ads 系列名，
 * 带 UTM 活动是 utm_campaign）。其它渠道同一字段存的是域名 / 搜索引擎 / 首页 URL，
 * 不能当 campaign。与插件 hubspot.labels.ts 的 CAMPAIGN_SOURCES 保持一致。
 */
const CAMPAIGN_SOURCES = new Set([
  'PAID_SEARCH',
  'PAID_SOCIAL',
  'OTHER_CAMPAIGNS',
  'EMAIL_MARKETING',
]);

class HubspotHttpError extends Error {
  status?: number;
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * 读当前登录用户的归因字段（读哪几个字段由服务端写死）。查不到返回 null，其它失败抛错。
 */
const fetchHubspotContact = async () => {
  const { status, data: body } = await getHubspotAttribution();
  // 网关拦下（401/403）时没有响应体；服务端失败是 HTTP 200 + success:false
  if (!body?.success || !body.data) {
    throw new HubspotHttpError(
      `[hubspot] attribution read failed: HTTP ${status} ${body?.code ?? ''}`,
      status,
      body?.code
    );
  }
  return body.data.found ? body.data.properties : null;
};

const syncStateKey = (uid: string) => `zen_hs_attr_synced:${uid}`;
const todayStamp = () => new Date().toISOString().slice(0, 10);

/** 建档 / 归因合并是异步的，第一次没读到或读到 OFFLINE 时，隔这么久再查一次。 */
const RETRY_DELAY_MS = 10 * 60_000;
/**
 * 每用户每天最多查 HubSpot 的次数。上线首日实测：新注册用户当天刷新 3~13 次页面，
 * 只活在内存里的一次重试全被刷掉、当天再不补查，三成活跃用户因此没有归因。
 * 3 次足够覆盖「identify 建档要几分钟」加一次瞬态失败；配额上 500 活跃用户 × 3 次
 * 远低于共享 token 的 100 req/10s。
 */
const MAX_ATTEMPTS_PER_DAY = 3;

/**
 * 当天的同步进度，存 localStorage 而不是只在内存里：重试要能跨过页面刷新。
 * done 只在写入了非 OFFLINE 的归因后才置 true——「没查到」「OFFLINE」「报错」都不算
 * 定论，留给下一次。以前是查询前就落「今天查过」标记，任何瞬态失败都被放大成全天丢失。
 */
type SyncState = {
  day: string;
  done: boolean;
  attempts: number;
  /** 下一次允许查询的时刻；刷新页面后按剩余时间把定时器挂回去 */
  nextAt?: number;
};

const readSyncState = (uid: string): SyncState | undefined => {
  try {
    const raw = localStorage.getItem(syncStateKey(uid));
    if (!raw) return undefined;
    if (raw.startsWith('{')) return JSON.parse(raw) as SyncState;
    // 旧格式只存了日期字符串：当天视为已查满，升级当天不重复打 HubSpot
    return { day: raw, done: true, attempts: MAX_ATTEMPTS_PER_DAY };
  } catch {
    return undefined;
  }
};

const writeSyncState = (uid: string, state: SyncState) => {
  try {
    localStorage.setItem(syncStateKey(uid), JSON.stringify(state));
  } catch {
    /* localStorage 不可用时每次页面加载都会查一次，只是多几次请求 */
  }
};

/**
 * 全局只挂一个重试，并记着是替谁挂的。登出是纯 SPA 跳转（不刷新、不 reset posthog），
 * 定时器触发时可能已经换了账号：换人即作废，写入前还会再核对 posthog 当前身份。
 */
let retry: { uid: string; timer: ReturnType<typeof setTimeout> } | undefined;

const armRetry = (uid: string, email: string, delay: number) => {
  if (retry?.uid === uid) return;
  if (retry) clearTimeout(retry.timer);
  retry = {
    uid,
    timer: globalThis.setTimeout(() => {
      retry = undefined;
      void syncHubspotAttribution(uid, email);
    }, delay),
  };
};

/**
 * 登录后把 HubSpot 已经算好的归因写成 PostHog person 属性（hs_* 前缀），
 * 让 PostHog 里能按广告系列筛人、做漏斗。字段含义与取舍见 docs/hubspot.md。
 *
 * 频率：每用户每天最多 MAX_ATTEMPTS_PER_DAY 次查询，两次间隔 RETRY_DELAY_MS，
 * 进度存 localStorage（见 SyncState），写入非 OFFLINE 归因即止。每次尝试在**查询之前**
 * 先记账——并发标签页、查询中途关页都不会让次数失控；HubSpot 里没有记录的老账号和
 * 永久 OFFLINE 的那部分联系人每天最多也只花 3 次。共享的私有应用 token 只有
 * 100 req/10s，429 之后连注册的 upsert 一起失败；归因慢一天到位没有代价。
 *
 * 调用方（useAutoLogin）已等 posthog 初始化完成再进来；否则末尾的身份核对会拿到
 * undefined 而静默退出，这正是上线首日漏掉一批用户的原因之一。
 *
 * 用 $set 不用 $set_once：原始来源可能从 OFFLINE 变成有值，写死会钉住 OFFLINE。
 */
export async function syncHubspotAttribution(uid: string, email: string) {
  if (getRuntimeInfo().online !== true || !uid || !email) return;

  // 注册时没等到 2 分钟就走了的人，在这里把姓名补进 HubSpot
  await flushPendingSignup(email);

  const today = todayStamp();
  const prev = readSyncState(uid);
  const state: SyncState =
    prev?.day === today
      ? {
          ...prev,
          done: prev.done === true,
          attempts: Number(prev.attempts) || 0,
        }
      : { day: today, done: false, attempts: 0 };
  if (state.done || state.attempts >= MAX_ATTEMPTS_PER_DAY) return;

  // 上一次查询定下的等待还没到：把定时器挂回去（页面刷新会丢掉内存里的那个）
  const wait = (state.nextAt ?? 0) - Date.now();
  if (wait > 0) {
    armRetry(uid, email, wait);
    return;
  }

  state.attempts += 1;
  state.nextAt = Date.now() + RETRY_DELAY_MS;
  writeSyncState(uid, state);

  const scheduleRetry = () => {
    if (state.attempts < MAX_ATTEMPTS_PER_DAY) {
      armRetry(uid, email, RETRY_DELAY_MS);
    }
  };

  try {
    const p = await fetchHubspotContact();
    const source = p?.hs_analytics_source || undefined;
    // 还没这个人，或建档了但归因还没算出来（注册后几分钟内常见）：不写空壳，稍后再试
    if (!p || !source) {
      scheduleRetry();
      return;
    }

    // 写之前确认 PostHog 当前身份还是这个人（await 期间可能已登出 / 换账号）
    if (posthog.get_distinct_id() !== uid) return;

    const detail1 = p.hs_analytics_source_data_1 || undefined;
    const props: Record<string, string | undefined> = {
      hs_source: source,
      hs_campaign: CAMPAIGN_SOURCES.has(source) ? detail1 : undefined,
      hs_source_detail: detail1,
      hs_source_detail_2: p.hs_analytics_source_data_2 || undefined,
      hs_first_url: p.hs_analytics_first_url || undefined,
      hs_attribution: p.hs_analytics_source_composite_data || undefined,
      hs_synced_at: new Date().toISOString(),
    };
    /**
     * 只带有值的 key。$set 是合并语义，缺的 key 不会动 PostHog 里已有的值——
     * 这里刻意不做 $unset：HubSpot 的原始来源只会从 OFFLINE 变成有值，不会倒回去，
     * 所以后一次读到的永远不比前一次少；一旦某次读到半截数据，宁可留旧值也不清空。
     */
    posthog.setPersonProperties(
      Object.fromEntries(
        Object.entries(props).filter(([, v]) => v !== undefined)
      )
    );

    // OFFLINE 也写（与「没同步过」区分开），但归因可能几分钟后才合并进来，再看一次
    if (source === 'OFFLINE') {
      scheduleRetry();
      return;
    }
    state.done = true;
    writeSyncState(uid, state);
  } catch (e) {
    trackingEvent({
      eventId: 'login_hubspot_AttributionSyncFailed',
      trackingType: 'error',
      customData: JSON.stringify({
        status: e instanceof HubspotHttpError ? e.status : undefined,
        code: e instanceof HubspotHttpError ? e.code : undefined,
        message: e instanceof Error ? e.message : String(e),
      }),
    });
    // Failed to fetch 这类瞬态失败值得再试，次数由 MAX_ATTEMPTS_PER_DAY 兜底
    scheduleRetry();
  }
}

export const useHubspotPageViewTracker = () => {
  const location = useLocation();
  const user = useUser();

  // online 判断收进 effect：原先在 hook 之前提前 return，属于条件性调用 hook
  useDebounceEffect(
    () => {
      pushIdentity(
        user?.uid,
        user?.username,
        `${location.pathname}${location.search}`
      );
    },
    /**
     * user 必须进 deps：自动登录晚于首屏渲染，只依赖 location 的话，落地页那次
     * identify 会因为当时还没拿到 email 而被永久跳过（location 不再变就不会重跑）。
     * 取标量而非 user 对象，避免引用变化引起无意义的重跑。
     */
    [location, user?.uid, user?.username],
    { wait: 500 }
  );
};
